import {Button, Link} from '@primer/react'
import type {ReactNode} from 'react'

import styles from './ErrorPageTemplateComponent.module.css'

export type ErrorPageTemplateStatusCode = '403' | '410' | '420' | '429' | '502' | '503'
export type ErrorPageTemplateNamedType = 'maintenance' | 'offline' | 'switching_runtime'
export type ErrorPageTemplateType = ErrorPageTemplateStatusCode | ErrorPageTemplateNamedType

export type ErrorPageTemplateComponentProps = {
  type?: ErrorPageTemplateType
  isEmbedded?: boolean
  isEnterprise?: boolean
  returnTo?: string
}

type ErrorPageTemplateContent = {
  status: string
  title: string
  paragraphs: Array<{
    content: ReactNode
    key: string
  }>
  actions?: Array<{
    href: string
    label: string
    variant?: 'primary'
  }>
}

const contactSupportUrl = 'https://support.github.com/contact'
const githubBlogUrl = 'https://github.blog/'
const githubStatusUrl = 'https://githubstatus.com'
const githubStatusSocialUrl = 'https://x.com/githubstatus'
const stafftoolsJitUrl = 'https://jit-okta-bouncer.githubapp.com/stafftools'
const stafftoolsEntitlementsUrl = 'https://thehub.github.com/engineering/security/entitlements/stafftools-entitlements'
const termsOfServiceUrl = 'https://docs.github.com/en/site-policy/github-terms/github-terms-of-service'
const stafftoolsAccessArticleTitle = ['Getting', 'Access', 'to', 'Stafftools'].join(' ')

function signInHref(returnTo?: string) {
  return returnTo ? `/login?return_to=${encodeURIComponent(returnTo)}` : '/login'
}

function footerActions(): ErrorPageTemplateContent['actions'] {
  return [
    {href: contactSupportUrl, label: 'Contact Support'},
    {href: githubStatusUrl, label: 'GitHub Status'},
    {href: githubStatusSocialUrl, label: '@githubstatus'},
  ]
}

function getTemplateContent({
  type,
  isEnterprise,
  returnTo,
}: Required<Pick<ErrorPageTemplateComponentProps, 'type'>> &
  Pick<ErrorPageTemplateComponentProps, 'isEnterprise' | 'returnTo'>): ErrorPageTemplateContent {
  if (type === '403') {
    return {
      status: '403',
      title: 'Forbidden',
      paragraphs: [
        {
          content: 'Sorry!',
          key: 'sorry',
        },
        {
          content: (
            <>
              You don&apos;t have access to this page. If you believe this is an error, you can try to{' '}
              <Link inline href={stafftoolsJitUrl}>
                JIT into stafftools
              </Link>
              .
            </>
          ),
          key: 'jit-stafftools',
        },
        {
          content: (
            <>
              If you need to request access, please check the{' '}
              <Link inline href={stafftoolsEntitlementsUrl}>
                {stafftoolsAccessArticleTitle}
              </Link>{' '}
              article for instructions.
            </>
          ),
          key: 'request-stafftools-access',
        },
      ],
    }
  }

  if (type === '410') {
    return {
      status: '410',
      title: 'Feature Gone',
      paragraphs: [
        {
          content: (
            <>
              Sorry, but this feature has been retired. Check out the{' '}
              <Link inline href={githubBlogUrl}>
                GitHub Blog
              </Link>{' '}
              for more information.
            </>
          ),
          key: 'feature-retired',
        },
      ],
      actions: footerActions(),
    }
  }

  if (type === '420') {
    return {
      status: '420',
      title: 'Whoa there!',
      paragraphs: [
        {
          content: 'Too many requests are being made from your IP address. Wait a minute or two and try again.',
          key: 'too-many-ip-requests',
        },
        {
          content: (
            <>
              Increase this rate limit by{' '}
              <Link inline href={signInHref(returnTo)}>
                signing in
              </Link>
              .
            </>
          ),
          key: 'increase-rate-limit',
        },
      ],
      actions: footerActions(),
    }
  }

  if (type === '502') {
    return {
      status: '502',
      title: 'Bad gateway',
      paragraphs: [
        {
          content: 'GitHub is taking too long to respond. Please try again in a few minutes.',
          key: 'bad-gateway',
        },
      ],
      actions: footerActions(),
    }
  }

  if (type === '503') {
    return {
      status: '503',
      title: 'Service unavailable',
      paragraphs: [
        {
          content: 'GitHub is temporarily unavailable. Please try again in a few minutes.',
          key: 'service-unavailable',
        },
      ],
      actions: footerActions(),
    }
  }

  if (type === 'maintenance') {
    return {
      status: '503',
      title: 'Maintenance in progress',
      paragraphs: [
        {
          content: 'GitHub is currently undergoing scheduled maintenance. Please check GitHub Status for updates.',
          key: 'maintenance',
        },
      ],
      actions: footerActions(),
    }
  }

  if (type === 'offline') {
    return {
      status: 'Offline',
      title: 'You are offline',
      paragraphs: [
        {
          content: 'It looks like you are offline. Check your connection and try again.',
          key: 'offline',
        },
      ],
    }
  }

  if (type === 'switching_runtime') {
    return {
      status: '503',
      title: 'Switching runtime',
      paragraphs: [
        {
          content: 'GitHub is switching runtime environments. Please refresh this page in a few moments.',
          key: 'switching-runtime',
        },
      ],
    }
  }

  if (isEnterprise) {
    return {
      status: '429',
      title: 'Whoa there!',
      paragraphs: [
        {
          content: (
            <>
              You have exceeded a secondary rate limit. Please wait a few minutes before you try again. For more on
              scraping GitHub and how it may affect your rights, please review our{' '}
              <Link inline href={termsOfServiceUrl}>
                Terms of Service
              </Link>
              .
            </>
          ),
          key: 'enterprise-secondary-rate-limit',
        },
      ],
    }
  }

  return {
    status: '429',
    title: 'Too many requests',
    paragraphs: [
      {
        content: (
          <>
            You have exceeded a secondary rate limit. Please wait a few minutes before you try again; in some cases this
            may take up to an hour.{' '}
            <Link inline href={signInHref(returnTo)}>
              Signing in
            </Link>{' '}
            may provide a higher rate limit if you are not already signed in. For more on scraping GitHub and how it may
            affect your rights, please review our{' '}
            <Link inline href={termsOfServiceUrl}>
              Terms of Service
            </Link>
            .
          </>
        ),
        key: 'secondary-rate-limit',
      },
    ],
    actions: footerActions(),
  }
}

export function ErrorPageTemplateComponent({
  type = '403',
  isEmbedded = false,
  isEnterprise = false,
  returnTo,
}: ErrorPageTemplateComponentProps) {
  const RootElement = isEmbedded ? 'div' : 'main'
  const content = getTemplateContent({type, isEnterprise, returnTo})
  const domId = `error-page-${type.replace('_', '-')}`
  const titleId = `${domId}-title`

  return (
    <RootElement id={domId} className={styles.ErrorPageTemplate}>
      <section className={styles.Container} aria-labelledby={titleId}>
        <p className={styles.StatusCode}>{content.status}</p>
        <h1 id={titleId} className={styles.Title}>
          {content.title}
        </h1>
        {content.paragraphs.map(paragraph => (
          <p className={styles.Description} key={paragraph.key}>
            {paragraph.content}
          </p>
        ))}
        {content.actions ? (
          <div className={styles.Actions}>
            {content.actions.map(action => {
              const key = `${action.label}-${action.href}`
              return action.variant === 'primary' ? (
                <Button as="a" href={action.href} key={key} variant="primary">
                  {action.label}
                </Button>
              ) : (
                <Link href={action.href} key={key}>
                  {action.label}
                </Link>
              )
            })}
          </div>
        ) : null}
      </section>
    </RootElement>
  )
}
